import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls, Environment, Html, Text, ContactShadows } from "@react-three/drei";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import {
  Boxes,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Eye,
  Grid3x3,
  Search,
  Camera as CameraIcon,
  Move3d,
  ChevronsUpDown,
  Maximize2,
  Minimize2,
  Play,
  Pause,
  ArrowDownToLine,
  ArrowUpFromLine,
} from "lucide-react";

/**
 * 3D warehouse floor: 108 physical sehpas arranged as 6 columns × 18 rows.
 *
 * Column layout matches the two-sided rack pairs the user uses:
 *   col 1 → side1 = AX, side2 = A
 *   col 2 → B / C     col 3 → D / E
 *   col 4 → F / G     col 5 → H / I     col 6 → K / L
 *
 * Row layout is the slot index 1..18. Each cell is ONE physical sehpa that
 * holds glass on both outer faces, so the cell at (col=1, row=5) stores
 * "AX-5" on its right face and "A-5" on its left face.
 *
 * Camera looks down at the whole grid; clicking any sehpa (or a slot in the
 * side panel) activates it and shows both faces' contents.
 */

const COLUMNS = [
  { col: 1, side1: "A", side2: "AX" },
  { col: 2, side1: "B", side2: "C" },
  { col: 3, side1: "D", side2: "E" },
  { col: 4, side1: "F", side2: "G" },
  { col: 5, side1: "H", side2: "I" },
  { col: 6, side1: "K", side2: "L" },
];
const SIDE_CODES = ["AX", "A", "B", "C", "D", "E", "F", "G", "H", "I", "K", "L"];
const ROWS = 18;
// Slots physically occupied by non-glass items (machine parts). These must
// never render as empty and are shown with an orange "MAKİNA" label in 3D.
const RESERVED_POSITIONS = new Set(
  Array.from({ length: 10 }, (_, i) => `AX-${i + 2}`), // AX-2 … AX-11
);
const isReserved = (side, slot) => RESERVED_POSITIONS.has(`${side}-${slot}`);
const COL_SPACING = 3.6;
const ROW_SPACING = 2.4;
const GRID_WIDTH = (COLUMNS.length - 1) * COL_SPACING;
const GRID_DEPTH = (ROWS - 1) * ROW_SPACING;
const GRID_START_X = -GRID_WIDTH / 2;
const GRID_START_Z = -GRID_DEPTH / 2;

/** World position of the sehpa at column/row */
function sehpaWorldPos(col, row) {
  const cIdx = col - 1;
  const rIdx = row - 1;
  return [GRID_START_X + cIdx * COL_SPACING, 0, GRID_START_Z + rIdx * ROW_SPACING];
}

/** Parse "A-13" → { side: "A", slot: 13 }. */
function parseSehpaCode(sehpaKonumu) {
  if (!sehpaKonumu) return null;
  const s = String(sehpaKonumu).trim().toUpperCase();
  for (const code of SIDE_CODES) {
    if (s === code) return { side: code, slot: 0 };
    if (s.startsWith(code + "-") || s.startsWith(code + " ")) {
      const n = parseInt(s.slice(code.length + 1), 10);
      return { side: code, slot: Number.isFinite(n) ? n : 0 };
    }
  }
  return null;
}

/**
 * Parse a size string like "160x255", "3210X2400", "4000 x 3210" into
 * {w, h} millimetres. Falls back to null when the format isn't detected.
 */
function parseEbat(ebat) {
  if (!ebat) return null;
  const m = String(ebat).toUpperCase().replace(/\s+/g, "").match(/(\d+)X(\d+)/);
  if (!m) return null;
  const a = parseInt(m[1], 10);
  const b = parseInt(m[2], 10);
  if (!a || !b) return null;
  // Convention in the codebase: first value is width, second is height.
  return { w: a, h: b };
}

// Sensible visual bounds tuned to the common commercial glass sizes:
//   width:  4000 · 4500 · 5100 · 6000 mm  (max = 6000)
//   height: 2400 · 2700 · 3210            (max = 3210)
const GLASS_MAX_H_3D = 2.4;   // world units for a 3210 mm tall glass
const GLASS_MAX_W_3D = 1.6;   // world units for a 6000 mm wide glass (Z axis)
const GLASS_MIN_H_3D = 0.9;   // world units for a 2400 mm tall glass
const GLASS_MIN_W_3D = 0.75;  // world units for a 4000 mm wide glass
const GLASS_H_MIN_MM = 2400;
const GLASS_H_MAX_MM = 3210;
const GLASS_W_MIN_MM = 4000;
const GLASS_W_MAX_MM = 6000;

/** Compute a representative glass size for a side's slot (max-height item). */
function representativeSize(slotItems) {
  let best = null;
  for (const it of slotItems || []) {
    const dim = parseEbat(it.ebat);
    if (!dim) continue;
    if (!best || dim.h > best.h) best = dim;
  }
  return best;
}

/**
 * Map real millimetre dimensions to world units so that the 4 standard glass
 * widths (4000·4500·5100·6000) and standard heights (2400·2700·3210) are
 * visually distinguishable at a glance.
 */
function toGlassScale(dim) {
  if (!dim) return { h: 1.6, w: 0.9 }; // fallback when ebat missing
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  const hRange = GLASS_H_MAX_MM - GLASS_H_MIN_MM;
  const wRange = GLASS_W_MAX_MM - GLASS_W_MIN_MM;
  const hNorm = clamp((dim.h - GLASS_H_MIN_MM) / hRange, 0, 1);
  const wNorm = clamp((dim.w - GLASS_W_MIN_MM) / wRange, 0, 1);
  return {
    h: GLASS_MIN_H_3D + (GLASS_MAX_H_3D - GLASS_MIN_H_3D) * hNorm,
    w: GLASS_MIN_W_3D + (GLASS_MAX_W_3D - GLASS_MIN_W_3D) * wNorm,
  };
}

/* ================================ Page shell ============================== */

export default function Warehouse3D() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCol, setActiveCol] = useState(1);
  const [activeRow, setActiveRow] = useState(1);
  const [activeSideIdx, setActiveSideIdx] = useState(0); // 0 = side1 (right), 1 = side2 (left)
  const [cameraPreset, setCameraPreset] = useState("iso"); // iso | top | front
  // Ticks whenever we want to force a camera re-focus animation (search or preset click).
  const [focusTick, setFocusTick] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [kioskOn, setKioskOn] = useState(false);
  const canvasWrapRef = useRef(null);
  const lastSeenMovementRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onFsChange = () =>
      setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      canvasWrapRef.current?.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.();
    }
  };

  useEffect(() => {
    setLoading(true);
    api
      .get("/inventory", { params: { limit: 5000, include_empty: false } })
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  }, []);

  // -------- Live movement notifications (fullscreen kiosk display) --------
  // Poll /movements every 15s. On new entries since the last seen id, show a
  // toast so operators watching the depot TV see incoming/outgoing activity.
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get("/movements", { params: { limit: 5 } });
        const items = data.items || [];
        if (!items.length) return;
        if (lastSeenMovementRef.current == null) {
          lastSeenMovementRef.current = items[0].id;
          return;
        }
        const last = lastSeenMovementRef.current;
        const fresh = [];
        for (const m of items) {
          if (m.id === last) break;
          fresh.push(m);
        }
        if (fresh.length) {
          lastSeenMovementRef.current = items[0].id;
          if (!cancelled) {
            for (const m of fresh.slice().reverse()) {
              const isIn = m.type === "giris";
              const title = isIn
                ? `📦 Cam girişi · ${m.adet} adet`
                : `🚚 Cam çıkışı · ${m.adet} adet`;
              const desc = `${m.isim || m.barkod || "-"} · ${m.sehpa_konumu || "-"}${m.musteri ? " · " + m.musteri : ""}`;
              toast[isIn ? "success" : "info"](title, {
                description: desc,
                duration: 6000,
              });
            }
          }
        }
      } catch (_) {
        /* ignore polling errors */
      }
    };
    poll();
    const id = setInterval(poll, 15000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Group by side letter + slot number.
  const bySide = useMemo(() => {
    const map = {};
    for (const code of SIDE_CODES) {
      map[code] = { items: [], totalAdet: 0, bySlot: {} };
      for (let n = 1; n <= ROWS; n++) map[code].bySlot[n] = { items: [], totalAdet: 0 };
    }
    for (const it of items) {
      const parsed = parseSehpaCode(it.sehpa_konumu);
      if (!parsed || !map[parsed.side]) continue;
      map[parsed.side].items.push(it);
      map[parsed.side].totalAdet += Number(it.adet || 0);
      const n = parsed.slot;
      if (n >= 1 && n <= ROWS) {
        map[parsed.side].bySlot[n].items.push(it);
        map[parsed.side].bySlot[n].totalAdet += Number(it.adet || 0);
      }
    }
    return map;
  }, [items]);

  const slotCapacity = useMemo(() => {
    let max = 0;
    for (const code of SIDE_CODES)
      for (let n = 1; n <= ROWS; n++)
        max = Math.max(max, bySide[code]?.bySlot[n]?.totalAdet || 0);
    return Math.max(max, 10);
  }, [bySide]);

  const currentCol = COLUMNS.find((c) => c.col === activeCol) || COLUMNS[0];
  const currentSideCode =
    activeSideIdx === 0 ? currentCol.side1 : currentCol.side2;
  const currentSideData = bySide[currentSideCode] || {
    items: [],
    totalAdet: 0,
    bySlot: {},
  };
  const currentSlotData = currentSideData.bySlot?.[activeRow] || {
    items: [],
    totalAdet: 0,
  };

  const nextCol = () => setActiveCol((c) => (c === COLUMNS.length ? 1 : c + 1));
  const prevCol = () => setActiveCol((c) => (c === 1 ? COLUMNS.length : c - 1));
  const nextRow = () => setActiveRow((r) => (r === ROWS ? 1 : r + 1));
  const prevRow = () => setActiveRow((r) => (r === 1 ? ROWS : r - 1));

  // Precompute an ordered list of populated (col, row, sideIdx) tuples for
  // kiosk mode; changes when inventory reloads.
  const populatedStops = useMemo(() => {
    const stops = [];
    for (const c of COLUMNS) {
      for (let r = 1; r <= ROWS; r++) {
        const rightAdet = bySide[c.side1]?.bySlot?.[r]?.totalAdet || 0;
        const leftAdet = bySide[c.side2]?.bySlot?.[r]?.totalAdet || 0;
        if (rightAdet > 0)
          stops.push({ col: c.col, row: r, sideIdx: 0, adet: rightAdet });
        if (leftAdet > 0)
          stops.push({ col: c.col, row: r, sideIdx: 1, adet: leftAdet });
      }
    }
    // sort by adet desc so the most interesting slots come first
    stops.sort((a, b) => b.adet - a.adet);
    return stops;
  }, [bySide]);

  // Kiosk mode: cycle to the next populated slot every 20 seconds.
  useEffect(() => {
    if (!kioskOn || populatedStops.length === 0) return;
    let idx = 0;
    const goTo = () => {
      const stop = populatedStops[idx % populatedStops.length];
      setActiveCol(stop.col);
      setActiveRow(stop.row);
      setActiveSideIdx(stop.sideIdx);
      setCameraPreset("focus");
      setFocusTick((t) => t + 1);
      idx += 1;
    };
    goTo();
    const id = setInterval(goTo, 20000);
    return () => clearInterval(id);
  }, [kioskOn, populatedStops]);

  return (
    <div className="space-y-4">
      <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)] p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Boxes size={22} className="text-[color:var(--primary)]" />
          <div className="ticker text-[color:var(--text-3)]">/// 3D DEPO</div>
        </div>

        <GoToSearchBox
          items={items}
          onPick={(loc) => {
            setActiveCol(loc.col);
            setActiveRow(loc.row);
            setActiveSideIdx(loc.sideIdx);
            setCameraPreset("focus");
            setFocusTick((t) => t + 1);
          }}
        />

        <div className="flex hard border-2 border-[color:var(--border)] overflow-hidden ml-2">
          {[
            { id: "iso", label: "İzometrik", icon: Move3d },
            { id: "top", label: "Üstten", icon: ChevronsUpDown },
            { id: "front", label: "Önden", icon: CameraIcon },
          ].map((p) => (
            <button
              key={p.id}
              data-testid={`w3d-view-${p.id}`}
              onClick={() => {
                setCameraPreset(p.id);
                setFocusTick((t) => t + 1);
              }}
              className={`h-9 px-3 text-xs font-bold uppercase flex items-center gap-1 ${
                cameraPreset === p.id
                  ? "bg-[color:var(--primary)] text-white"
                  : "bg-white hover:bg-[color:var(--bg)]"
              }`}
            >
              <p.icon size={12} /> {p.label}
            </button>
          ))}
        </div>

        <button
          data-testid="w3d-kiosk-toggle"
          onClick={() => setKioskOn((v) => !v)}
          title={kioskOn ? "Kiosk modunu durdur" : "Kiosk modu: dolu slotları 20 sn'de bir gez"}
          className={`h-9 px-3 hard border-2 text-xs font-bold uppercase flex items-center gap-1 ${
            kioskOn
              ? "border-[color:var(--success)] bg-[color:var(--success)] text-white"
              : "border-[color:var(--border)] hover:border-[color:var(--primary)]"
          }`}
        >
          {kioskOn ? <Pause size={12} /> : <Play size={12} />}
          {kioskOn ? "Kiosk Aktif" : "Kiosk"}
        </button>

        <button
          data-testid="w3d-fullscreen"
          onClick={toggleFullscreen}
          title={isFullscreen ? "Tam ekranı kapat" : "Tam ekran"}
          className="h-9 px-3 hard border-2 border-[color:var(--border)] hover:border-[color:var(--primary)] text-xs font-bold uppercase flex items-center gap-1"
        >
          {isFullscreen ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
          {isFullscreen ? "Kapat" : "Tam Ekran"}
        </button>

        <div className="ml-auto ticker text-[10px] text-[color:var(--text-3)]">
          AKTİF: {currentCol.side1}/{currentCol.side2} · SIRA {activeRow}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_400px]">
        <div
          ref={canvasWrapRef}
          data-testid="w3d-canvas-wrap"
          className={`hard border-2 border-[color:var(--border)] relative overflow-hidden ${
            isFullscreen ? "bg-[#0b1220]" : ""
          }`}
          style={{
            height: isFullscreen ? "100vh" : "min(78vh, 780px)",
          }}
        >
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm z-10">
              Stok yükleniyor…
            </div>
          )}

          {isFullscreen && (
            <button
              data-testid="w3d-fullscreen-exit"
              onClick={toggleFullscreen}
              title="Tam ekrandan çık"
              className="absolute top-4 right-4 z-20 h-11 w-11 hard bg-black/60 hover:bg-black/80 border-2 border-white/20 text-white flex items-center justify-center"
            >
              <Minimize2 size={18} />
            </button>
          )}

          {kioskOn && (
            <div
              data-testid="w3d-kiosk-indicator"
              className="absolute top-4 left-4 z-20 hard bg-black/70 border-2 border-[color:var(--success)] px-3 py-1.5 flex items-center gap-2"
            >
              <span className="inline-block w-2 h-2 rounded-full bg-[color:var(--success)] animate-pulse" />
              <span className="text-white text-xs font-bold uppercase tracking-wider">
                Kiosk · {populatedStops.length} slot
              </span>
            </div>
          )}
          <Canvas
            shadows
            camera={{ position: [0, 20, 32], fov: 42 }}
            gl={{ antialias: true }}
          >
            <color attach="background" args={["#0b1220"]} />
            <fog attach="fog" args={["#0b1220", 40, 100]} />
            <ambientLight intensity={0.55} />
            <directionalLight
              castShadow
              position={[18, 30, 20]}
              intensity={1.2}
              shadow-mapSize={[1024, 1024]}
            />
            <Suspense fallback={null}>
              <Scene
                bySide={bySide}
                slotCapacity={slotCapacity}
                activeCol={activeCol}
                activeRow={activeRow}
                onSehpaClick={(col, row) => {
                  setActiveCol(col);
                  setActiveRow(row);
                }}
              />
              <ContactShadows
                position={[0, -0.01, 0]}
                opacity={0.55}
                blur={2.5}
                far={50}
                scale={80}
              />
              <Environment preset="warehouse" />
            </Suspense>
            <OrbitControls
              makeDefault
              enablePan
              minPolarAngle={0.15}
              maxPolarAngle={Math.PI / 2.1}
              minDistance={10}
              maxDistance={70}
            />
            <CameraController
              preset={cameraPreset}
              activeCol={activeCol}
              activeRow={activeRow}
              activeSideIdx={activeSideIdx}
              tick={focusTick}
            />
          </Canvas>

          {/* Quick column selector overlay */}
          <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-1.5 justify-center">
            {COLUMNS.map((c) => (
              <button
                key={c.col}
                data-testid={`w3d-column-${c.col}`}
                onClick={() => setActiveCol(c.col)}
                className={`h-9 px-3 hard border-2 text-[11px] font-black tracking-wider transition-colors ${
                  activeCol === c.col
                    ? "border-[color:var(--primary)] bg-[color:var(--primary)] text-white"
                    : "border-white/30 bg-white/10 text-white hover:bg-white/20"
                }`}
              >
                SÜTUN {c.col}: {c.side1}–{c.side2}
              </button>
            ))}
          </div>
        </div>

        <SideInfoScreen
          col={currentCol}
          activeSideIdx={activeSideIdx}
          setActiveSideIdx={setActiveSideIdx}
          sideCode={currentSideCode}
          sideData={currentSideData}
          activeRow={activeRow}
          setActiveRow={setActiveRow}
          slotData={currentSlotData}
          slotCapacity={slotCapacity}
          onPrevCol={prevCol}
          onNextCol={nextCol}
          onPrevRow={prevRow}
          onNextRow={nextRow}
          onOpenList={() =>
            navigate(
              `/stok?sehpa=${encodeURIComponent(`${currentSideCode}-${activeRow}`)}`,
            )
          }
        />
      </div>
    </div>
  );
}

/* ============================ 3D scene contents =========================== */

function Scene({ bySide, slotCapacity, activeCol, activeRow, onSehpaClick }) {
  const startX = GRID_START_X;
  const startZ = GRID_START_Z;
  const totalWidth = GRID_WIDTH;
  const totalDepth = GRID_DEPTH;

  return (
    <group>
      {/* Warehouse floor */}
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
        <planeGeometry args={[totalWidth + 10, totalDepth + 10]} />
        <meshStandardMaterial color="#1f2937" roughness={0.9} />
      </mesh>
      <gridHelper
        args={[totalWidth + 10, Math.round((totalWidth + 10) / 2), "#334155", "#1f2937"]}
        position={[0, 0.01, 0]}
      />

      {/* Column stencils at the front edge */}
      {COLUMNS.map((c, cIdx) => (
        <group key={c.col}>
          <Text
            position={[
              startX + cIdx * COL_SPACING,
              0.05,
              startZ - 1.2,
            ]}
            rotation={[-Math.PI / 2, 0, 0]}
            fontSize={0.5}
            color="#e2e8f0"
            anchorX="center"
            anchorY="middle"
          >
            {c.side1}/{c.side2}
          </Text>
        </group>
      ))}
      {/* Row numbers on the left edge */}
      {Array.from({ length: ROWS }).map((_, rIdx) => (
        <Text
          key={rIdx}
          position={[startX - 1.4, 0.05, startZ + rIdx * ROW_SPACING]}
          rotation={[-Math.PI / 2, 0, -Math.PI / 2]}
          fontSize={0.35}
          color="#94a3b8"
          anchorX="center"
          anchorY="middle"
        >
          {`${rIdx + 1}`}
        </Text>
      ))}

      {COLUMNS.map((c, cIdx) =>
        Array.from({ length: ROWS }).map((_, rIdx) => {
          const x = startX + cIdx * COL_SPACING;
          const z = startZ + rIdx * ROW_SPACING;
          const row = rIdx + 1;
          const active = activeCol === c.col && activeRow === row;
          const rightSlot = bySide[c.side1]?.bySlot[row] || { totalAdet: 0, items: [] };
          const leftSlot = bySide[c.side2]?.bySlot[row] || { totalAdet: 0, items: [] };
          return (
            <Sehpa
              key={`${c.col}-${row}`}
              position={[x, 0, z]}
              col={c}
              row={row}
              rightAdet={rightSlot.totalAdet}
              leftAdet={leftSlot.totalAdet}
              rightItems={rightSlot.items}
              leftItems={leftSlot.items}
              rightReserved={isReserved(c.side1, row)}
              leftReserved={isReserved(c.side2, row)}
              slotCapacity={slotCapacity}
              active={active}
              onClick={() => onSehpaClick(c.col, row)}
            />
          );
        }),
      )}
    </group>
  );
}

/**
 * One physical sehpa: two vertical support posts + pallet base + glass sheets
 * leaning slightly outward on both faces (matches the user's reference photo:
 * upright frame, glass fans outward rather than an A-frame).
 */
function Sehpa({ position, col, row, rightAdet, leftAdet, rightItems, leftItems, rightReserved, leftReserved, slotCapacity, active, onClick }) {
  const [hover, setHover] = useState(false);
  const frameColor = active ? "#f59e0b" : hover ? "#cbd5e1" : "#64748b";
  const postH = 2.0;
  const baseW = 1.4;
  const baseD = 1.2;

  const RESERVED_COLOR = "#f97316"; // orange — non-glass content
  const rightColor = rightReserved
    ? RESERVED_COLOR
    : colorForLoad(rightAdet, slotCapacity);
  const leftColor = leftReserved
    ? RESERVED_COLOR
    : colorForLoad(leftAdet, slotCapacity);

  const rightOpacity = rightReserved ? 0.85 : rightAdet > 0 ? 0.9 : 0.12;
  const leftOpacity = leftReserved ? 0.85 : leftAdet > 0 ? 0.9 : 0.12;

  // Shape glass panels using the slot's largest actual glass dimensions.
  const rightSize = toGlassScale(representativeSize(rightItems));
  const leftSize = toGlassScale(representativeSize(leftItems));

  return (
    <group
      position={position}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      onPointerOver={(e) => {
        e.stopPropagation();
        setHover(true);
      }}
      onPointerOut={(e) => {
        e.stopPropagation();
        setHover(false);
      }}
    >
      {/* Pallet base */}
      <mesh position={[0, 0.1, 0]} castShadow receiveShadow>
        <boxGeometry args={[baseW, 0.2, baseD]} />
        <meshStandardMaterial color="#78350f" roughness={0.9} />
      </mesh>

      {/* Two vertical posts */}
      <mesh position={[0, postH / 2 + 0.2, -baseD / 2 + 0.06]} castShadow>
        <boxGeometry args={[0.1, postH, 0.1]} />
        <meshStandardMaterial color={frameColor} metalness={0.5} roughness={0.4} />
      </mesh>
      <mesh position={[0, postH / 2 + 0.2, baseD / 2 - 0.06]} castShadow>
        <boxGeometry args={[0.1, postH, 0.1]} />
        <meshStandardMaterial color={frameColor} metalness={0.5} roughness={0.4} />
      </mesh>
      {/* Cross beam */}
      <mesh position={[0, postH + 0.15, 0]} castShadow>
        <boxGeometry args={[0.08, 0.08, baseD]} />
        <meshStandardMaterial color={frameColor} metalness={0.5} roughness={0.4} />
      </mesh>

      {/* Right side glass (side1) — leans INWARD, size reflects real glass dims */}
      <mesh
        position={[baseW / 2 + 0.05, rightSize.h / 2 + 0.2, 0]}
        rotation={[0, 0, 0.18]}
        castShadow
      >
        <boxGeometry args={[0.06, rightSize.h, rightSize.w]} />
        <meshStandardMaterial
          color={rightColor}
          transparent
          opacity={rightOpacity}
          metalness={0.35}
          roughness={0.15}
          emissive={rightColor}
          emissiveIntensity={rightAdet > 0 ? 0.1 : 0}
        />
      </mesh>

      {/* Left side glass (side2) — leans INWARD */}
      <mesh
        position={[-baseW / 2 - 0.05, leftSize.h / 2 + 0.2, 0]}
        rotation={[0, 0, -0.18]}
        castShadow
      >
        <boxGeometry args={[0.06, leftSize.h, leftSize.w]} />
        <meshStandardMaterial
          color={leftColor}
          transparent
          opacity={leftOpacity}
          metalness={0.35}
          roughness={0.15}
          emissive={leftColor}
          emissiveIntensity={leftAdet > 0 ? 0.1 : 0}
        />
      </mesh>

      {/* Active outline */}
      {active && (
        <mesh position={[0, 0.03, 0]}>
          <ringGeometry args={[baseW * 0.75, baseW * 0.85, 32]} />
          <meshBasicMaterial color="#f59e0b" transparent opacity={0.85} />
        </mesh>
      )}

      {/* Slot label under the frame */}
      <Text
        position={[0, 0.22, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        fontSize={0.16}
        color={active ? "#f59e0b" : "#e2e8f0"}
        anchorX="center"
        anchorY="middle"
      >
        {`${col.side1}-${row} / ${col.side2}-${row}`}
      </Text>

      {(hover || active) && (
        <Html position={[0, postH + 0.6, 0]} center style={{ pointerEvents: "none" }}>
          <div
            style={{
              background: "rgba(15, 23, 42, 0.94)",
              color: "white",
              padding: "6px 10px",
              fontSize: 11,
              fontWeight: 700,
              whiteSpace: "nowrap",
              border: "1px solid rgba(255,255,255,0.15)",
              textAlign: "center",
            }}
          >
            <div style={{ opacity: 0.7, fontSize: 9, letterSpacing: 1 }}>
              SÜTUN {col.col} · SIRA {row}
            </div>
            <div>
              {col.side1}-{row}: {rightReserved ? "MAKİNA" : rightAdet} · {col.side2}-{row}: {leftReserved ? "MAKİNA" : leftAdet}
            </div>
          </div>
        </Html>
      )}

      {/* Persistent "MAKİNA" badge above reserved sides so operators
          spot non-glass shelves even without hovering. */}
      {(rightReserved || leftReserved) && (
        <Html position={[0, postH + 0.15, 0]} center style={{ pointerEvents: "none" }}>
          <div
            style={{
              background: "#f97316",
              color: "#0f172a",
              padding: "2px 6px",
              fontSize: 9,
              fontWeight: 900,
              letterSpacing: 1,
              border: "1px solid #c2410c",
            }}
          >
            MAKİNA
          </div>
        </Html>
      )}
    </group>
  );
}

function colorForLoad(adet, capacity) {
  const r = capacity ? adet / capacity : 0;
  if (adet === 0) return "#334155";
  if (r < 0.33) return "#22c55e";
  if (r < 0.66) return "#eab308";
  return "#ef4444";
}

/* ============================== Info screen =============================== */

function SideInfoScreen({
  col,
  activeSideIdx,
  setActiveSideIdx,
  sideCode,
  sideData,
  activeRow,
  setActiveRow,
  slotData,
  slotCapacity,
  onPrevCol,
  onNextCol,
  onPrevRow,
  onNextRow,
  onOpenList,
}) {
  const groups = useMemo(() => {
    const byKey = {};
    for (const it of slotData?.items || []) {
      const key = `${it.isim || "-"}|${it.ebat || "-"}|${it.barkod}`;
      if (!byKey[key]) {
        byKey[key] = {
          isim: it.isim || "-",
          ebat: it.ebat || "-",
          barkod: it.barkod,
          adet: 0,
          kalite: it.urun_kalitesi || "-",
        };
      }
      byKey[key].adet += Number(it.adet || 0);
    }
    return Object.values(byKey).sort((a, b) => b.adet - a.adet);
  }, [slotData]);

  return (
    <div
      className="hard border-2 border-[color:var(--border)] bg-[color:var(--surface)] flex flex-col overflow-hidden"
      style={{ maxHeight: "min(78vh, 780px)" }}
    >
      {/* Column navigator */}
      <div className="p-3 border-b-2 border-[color:var(--border)] bg-[color:var(--bg)]">
        <div className="flex items-center gap-2 justify-between">
          <button
            data-testid="w3d-prev-sehpa"
            onClick={onPrevCol}
            className="h-8 w-8 hard border border-[color:var(--border)] hover:border-[color:var(--primary)] flex items-center justify-center"
            title="Önceki sütun"
          >
            <ChevronLeft size={14} />
          </button>
          <div className="text-center flex-1">
            <div className="ticker text-[10px] text-[color:var(--text-3)]">
              SÜTUN {col.col}
            </div>
            <div data-testid="w3d-current-sehpa" className="font-black text-lg">
              {col.side1}–{col.side2}
            </div>
          </div>
          <button
            data-testid="w3d-next-sehpa"
            onClick={onNextCol}
            className="h-8 w-8 hard border border-[color:var(--border)] hover:border-[color:var(--primary)] flex items-center justify-center"
            title="Sonraki sütun"
          >
            <ChevronRight size={14} />
          </button>
        </div>

        <div className="mt-3 grid grid-cols-[auto_1fr_auto] gap-2 items-center">
          <button
            data-testid="w3d-arrow-left"
            onClick={() => setActiveSideIdx(1)}
            className={`h-11 px-3 hard border-2 flex items-center gap-1 font-bold text-xs ${
              activeSideIdx === 1
                ? "border-[color:var(--primary)] bg-[color:var(--primary)] text-white"
                : "border-[color:var(--border)] hover:border-[color:var(--primary)]"
            }`}
          >
            <ChevronLeft size={14} /> {col.side2}
          </button>
          <div className="hard bg-[color:var(--bg)] border-2 border-[color:var(--border)] py-2 text-center">
            <div className="ticker text-[9px] text-[color:var(--text-3)]">
              Aktif yan
            </div>
            <div
              data-testid="w3d-active-side"
              className="font-mono font-black text-2xl text-[color:var(--primary)]"
            >
              {sideCode}
            </div>
          </div>
          <button
            data-testid="w3d-arrow-right"
            onClick={() => setActiveSideIdx(0)}
            className={`h-11 px-3 hard border-2 flex items-center gap-1 font-bold text-xs ${
              activeSideIdx === 0
                ? "border-[color:var(--primary)] bg-[color:var(--primary)] text-white"
                : "border-[color:var(--border)] hover:border-[color:var(--primary)]"
            }`}
          >
            {col.side1} <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Row navigator + slot grid */}
      <div className="p-3 border-b border-[color:var(--border)]">
        <div className="flex items-center justify-between mb-2 gap-2">
          <div className="ticker text-[10px] text-[color:var(--text-3)]">
            SIRA (SLOT 1..18)
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid="w3d-prev-row"
              onClick={onPrevRow}
              className="h-7 w-7 hard border border-[color:var(--border)] hover:border-[color:var(--primary)] flex items-center justify-center"
              title="Önceki sıra"
            >
              <ChevronLeft size={12} />
            </button>
            <span className="text-sm font-mono font-black px-2">
              {activeRow}
            </span>
            <button
              data-testid="w3d-next-row"
              onClick={onNextRow}
              className="h-7 w-7 hard border border-[color:var(--border)] hover:border-[color:var(--primary)] flex items-center justify-center"
              title="Sonraki sıra"
            >
              <ChevronRight size={12} />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-6 gap-1">
          {Array.from({ length: ROWS }).map((_, i) => {
            const n = i + 1;
            const adet = sideData.bySlot?.[n]?.totalAdet || 0;
            const isSel = activeRow === n;
            const bg =
              adet === 0
                ? "bg-white text-[color:var(--text-3)]"
                : adet / slotCapacity < 0.33
                  ? "bg-[color:var(--success-bg)] text-[color:var(--success)]"
                  : adet / slotCapacity < 0.66
                    ? "bg-[color:var(--warning-bg)] text-[color:var(--warning)]"
                    : "bg-[color:var(--error-bg)] text-[color:var(--error)]";
            return (
              <button
                key={n}
                data-testid={`w3d-slot-${n}`}
                onClick={() => setActiveRow(n)}
                title={`${sideCode}-${n} · ${adet} adet`}
                className={`aspect-square hard border-2 text-[10px] font-bold flex flex-col items-center justify-center transition-colors ${
                  isSel
                    ? "border-[color:var(--primary)] ring-2 ring-[color:var(--primary)]"
                    : "border-[color:var(--border)]"
                } ${bg}`}
              >
                <span className="font-black">{n}</span>
                <span className="font-mono text-[9px] opacity-80">{adet}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Slot info + Stoğa Git */}
      <div className="p-3 border-b border-[color:var(--border)] flex items-center gap-3 flex-wrap">
        <div className="text-sm">
          <span className="font-mono font-bold">
            {sideCode}-{activeRow}
          </span>{" "}
          slot:{" "}
          <b
            data-testid="w3d-total-adet"
            className="font-mono text-lg text-[color:var(--primary)]"
          >
            {slotData?.totalAdet || 0}
          </b>{" "}
          adet
        </div>
        <div className="text-xs text-[color:var(--text-3)]">
          {groups.length} farklı cam
        </div>
        <button
          data-testid="w3d-open-list"
          onClick={onOpenList}
          className="ml-auto h-9 px-3 hard bg-[color:var(--primary)] text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1 hover:opacity-90"
        >
          <ExternalLink size={12} /> Stoğa Git
        </button>
      </div>

      {/* Items in the active slot */}
      <div className="flex-1 overflow-auto">
        {groups.length === 0 ? (
          <div className="p-8 text-center text-[color:var(--text-3)] text-sm">
            <Eye size={22} className="mx-auto mb-2 opacity-40" />
            {sideCode}-{activeRow} slotu boş.
          </div>
        ) : (
          <ul className="divide-y divide-[color:var(--border)]">
            {groups.map((g, i) => (
              <li
                key={i}
                data-testid="w3d-item-row"
                className="px-3 py-2 hover:bg-[color:var(--bg)]"
              >
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div
                      className="font-semibold text-sm truncate"
                      title={g.isim}
                    >
                      {g.isim}
                    </div>
                    <div className="text-[11px] text-[color:var(--text-3)] flex items-center gap-2 mt-0.5">
                      <span className="font-mono">{g.ebat}</span>
                      <span>·</span>
                      <span>{g.kalite}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-black text-lg text-[color:var(--success)]">
                      {g.adet}
                    </div>
                    <div className="ticker text-[9px] text-[color:var(--text-3)]">
                      ADET
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


/* =========================== Camera controller =========================== */

/**
 * Imperatively drives the default camera + OrbitControls target to match the
 * selected preset. Runs whenever `tick` bumps (search / preset button click).
 * Presets:
 *   - iso    → wide isometric view of the whole grid
 *   - top    → top-down orthographic-like view
 *   - front  → straight-on view from the front row
 *   - focus  → zoom close to the currently active sehpa slot
 */
function CameraController({ preset, activeCol, activeRow, activeSideIdx, tick }) {
  const { camera, controls } = useThree();
  const stateRef = useRef({ camPos: null, target: null, t0: 0, dur: 700 });

  useEffect(() => {
    if (!controls) return;
    let targetPos = [0, 1, 0];
    let camPos = [0, 22, 32];

    if (preset === "top") {
      targetPos = [0, 0, 0];
      camPos = [0.001, 42, 0.001];
    } else if (preset === "front") {
      targetPos = [0, 1.2, 0];
      camPos = [0, 6, 38];
    } else if (preset === "iso") {
      targetPos = [0, 1, 0];
      camPos = [22, 22, 26];
    } else if (preset === "focus") {
      const [x, , z] = sehpaWorldPos(activeCol, activeRow);
      targetPos = [x, 1.3, z];
      // Approach from the active side so glass is visible
      const sideX = activeSideIdx === 0 ? 4 : -4;
      camPos = [x + sideX, 4, z + 5];
    }

    stateRef.current = {
      fromCam: camera.position.clone(),
      fromTarget: controls.target.clone(),
      toCam: { x: camPos[0], y: camPos[1], z: camPos[2] },
      toTarget: { x: targetPos[0], y: targetPos[1], z: targetPos[2] },
      t0: performance.now(),
      dur: 650,
    };

    let raf = 0;
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);
    const step = () => {
      const s = stateRef.current;
      const t = Math.min(1, (performance.now() - s.t0) / s.dur);
      const k = easeOut(t);
      camera.position.set(
        s.fromCam.x + (s.toCam.x - s.fromCam.x) * k,
        s.fromCam.y + (s.toCam.y - s.fromCam.y) * k,
        s.fromCam.z + (s.toCam.z - s.fromCam.z) * k,
      );
      controls.target.set(
        s.fromTarget.x + (s.toTarget.x - s.fromTarget.x) * k,
        s.fromTarget.y + (s.toTarget.y - s.fromTarget.y) * k,
        s.fromTarget.z + (s.toTarget.z - s.fromTarget.z) * k,
      );
      controls.update();
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  return null;
}

/* =========================== Go-to search box ============================ */

/**
 * Type-ahead search over the whole inventory. Suggestions show barkod, isim,
 * ebat and the target sehpa location. Selecting a suggestion emits
 * { col, row, sideIdx } so the parent can focus the camera there.
 */
function GoToSearchBox({ items, onPick }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const suggestions = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (s.length < 2) return [];
    const out = [];
    for (const it of items) {
      const hay = `${it.barkod || ""} ${it.isim || ""} ${it.ebat || ""} ${it.sehpa_konumu || ""}`.toLowerCase();
      if (hay.includes(s)) {
        const parsed = parseSehpaCode(it.sehpa_konumu);
        if (!parsed) continue;
        const colDef = COLUMNS.find((c) => c.side1 === parsed.side || c.side2 === parsed.side);
        if (!colDef) continue;
        if (parsed.slot < 1 || parsed.slot > ROWS) continue;
        out.push({
          item: it,
          col: colDef.col,
          row: parsed.slot,
          sideIdx: colDef.side1 === parsed.side ? 0 : 1,
          sideCode: parsed.side,
        });
        if (out.length >= 12) break;
      }
    }
    return out;
  }, [q, items]);

  const pick = (sug) => {
    onPick({ col: sug.col, row: sug.row, sideIdx: sug.sideIdx });
    setQ(`${sug.sideCode}-${sug.row}  ·  ${sug.item.isim || sug.item.barkod}`);
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(suggestions.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      pick(suggestions[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className="relative flex-1 min-w-[260px] max-w-md ml-2">
      <Search
        size={14}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-3)]"
      />
      <input
        data-testid="w3d-goto-search"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder="Cama git… (barkod, isim veya konum)"
        className="w-full h-9 pl-8 pr-3 hard border-2 border-[color:var(--border)] bg-white text-sm focus:border-[color:var(--primary)] outline-none"
      />
      {open && suggestions.length > 0 && (
        <ul
          data-testid="w3d-goto-suggestions"
          className="absolute left-0 right-0 top-full mt-1 max-h-72 overflow-auto hard border-2 border-[color:var(--border)] bg-[color:var(--surface)] shadow-lg z-30"
        >
          {suggestions.map((sug, i) => (
            <li
              key={i}
              data-testid={`w3d-goto-suggestion-${i}`}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => pick(sug)}
              className={`px-3 py-2 cursor-pointer border-b border-[color:var(--border)] last:border-b-0 ${
                highlight === i ? "bg-[color:var(--bg)]" : ""
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="font-mono font-black text-xs bg-[color:var(--primary)] text-white px-2 py-0.5">
                  {sug.sideCode}-{sug.row}
                </span>
                <span className="font-mono text-[11px] text-[color:var(--text-3)]">
                  {sug.item.barkod}
                </span>
              </div>
              <div className="text-sm mt-1 truncate" title={sug.item.isim}>
                {sug.item.isim}
              </div>
              <div className="text-[11px] text-[color:var(--text-3)] mt-0.5">
                {sug.item.ebat} · {sug.item.adet} adet
              </div>
            </li>
          ))}
        </ul>
      )}
      {open && q.trim().length >= 2 && suggestions.length === 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 p-3 text-xs text-[color:var(--text-3)] hard border-2 border-[color:var(--border)] bg-[color:var(--surface)] z-30">
          Eşleşen cam bulunamadı.
        </div>
      )}
    </div>
  );
}
